import { ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { LanguageSwitcher } from './LanguageSwitcher'
import logo from '@/assets/logo.svg'

interface AdminLayoutProps {
  children: ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()

  const isActive = (path: string) => location.pathname.startsWith(path)

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
                  <img src={logo} alt="U-Like-It" className="h-10 w-auto hover:opacity-80 transition-opacity cursor-pointer" />
                </a>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t('app.title')}</h1>
                  <span className="text-xs text-gray-500">Admin</span>
                </div>
              </div>

              {/* Admin Menu */}
              <div className="hidden md:flex space-x-4">
                <button
                  onClick={() => navigate('/admin/applicants')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/applicants')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.applicants')}
                </button>
                <button
                  onClick={() => navigate('/admin/interviews')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/interviews')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Interviews
                </button>
                <button
                  onClick={() => navigate('/admin/scripts')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/scripts')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.scripts')}
                </button>
                <button
                  onClick={() => navigate('/admin/regions')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/regions')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.regions')}
                </button>
                <button
                  onClick={() => navigate('/admin/businesses')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/businesses')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.businesses')}
                </button>
                <button
                  onClick={() => navigate('/admin/visits')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/visits')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.visits')}
                </button>
                <button
                  onClick={() => navigate('/admin/compensation')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/compensation')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.compensation')}
                </button>
                <button
                  onClick={() => navigate('/admin/settings')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/settings')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('admin.settings')}
                </button>
                <button
                  onClick={() => window.open('/flyer', '_blank')}
                  className="px-3 py-2 rounded-md text-sm font-medium text-orange-600 hover:bg-orange-50"
                >
                  {t('admin.flyer')}
                </button>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                {user?.firstName} {user?.lastName}
              </span>
              <LanguageSwitcher />
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}
